import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronRight, ShieldCheck, Truck, Star, Award } from "lucide-react";
import { api, whatsappLink } from "@/api";
import { VehicleCard } from "@/components/VehicleCard";

const HERO_VIDEO = "https://customer-assets-0z36b82j.emergentagent.net/job_cmc-auto-altamura/artifacts/9676t3jo_Video%20Project%20131.mp4";
const HERO_DESKTOP_SLIDES = [
  "https://customer-assets-0z36b82j.emergentagent.net/job_cmc-auto-altamura/artifacts/k2m3xgtj_Gemini_Generated_Image_6489fr6489fr6489.jpg",
  "https://customer-assets-0z36b82j.emergentagent.net/job_cmc-auto-altamura/artifacts/ywbezxy1_Gemini_Generated_Image_980m57980m57980m.jpg",
  "https://customer-assets-0z36b82j.emergentagent.net/job_cmc-auto-altamura/artifacts/gdf37ale_Gemini_Generated_Image_tu7r20tu7r20tu7r.jpg",
];
const HERO_POSTER = "https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";
const HERO_TAGS = [
  "Utilitarie · SUV · Berline · Sportive",
  "Consegna in tutta Italia",
  "Finanziamenti personalizzati",
];

const REVIEWS = [];

const useCounter = (target, duration = 1600) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    let raf;
    let start = null;
    const step = (t) => {
      if (!start) start = t;
      const p = Math.min((t - start) / duration, 1);
      setN(Math.floor(p * target));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return n;
};

const Counter = ({ value, suffix = "", label }) => {
  const n = useCounter(value);
  return (
    <div className="text-center" data-testid="trust-counter">
      <div className="font-display text-4xl md:text-5xl font-extrabold gold-text tracking-wider">{n}{suffix}</div>
      <div className="text-xs md:text-sm text-zinc-400 tracking-widest uppercase mt-2">{label}</div>
    </div>
  );
};

const Home = () => {
  const [slide, setSlide] = useState(0);
  const [vehicles, setVehicles] = useState([]);
  const [brands, setBrands] = useState([]);
  const [f, setF] = useState({ marca: "", alimentazione: "", anno: "" });
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/vehicles").then((r) => setVehicles(r.data.slice(0, 6)));
    api.get("/vehicles/brands").then((r) => setBrands(r.data));
    const id = setInterval(() => setSlide((s) => (s + 1) % HERO_DESKTOP_SLIDES.length), 5000);
    return () => clearInterval(id);
    // Mount-only effect; api is module-level stable, HERO_DESKTOP_SLIDES is a constant
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const search = (e) => {
    e.preventDefault();
    const p = new URLSearchParams();
    if (f.marca) p.set("marca", f.marca);
    if (f.alimentazione) p.set("alimentazione", f.alimentazione);
    if (f.anno) p.set("anno_min", f.anno);
    navigate(`/parco-veicoli?${p.toString()}`);
  };

  return (
    <div>
      {/* HERO */}
      <section className="hero-section" data-testid="hero-section" style={{ minHeight: "70vh" }}>
        {/* Desktop: 3-image auto slider */}
        <div className="hero-video hidden md:block" style={{ position: "absolute", inset: 0 }}>
          {HERO_DESKTOP_SLIDES.map((src, i) => (
            <img
              key={src}
              src={src}
              alt={`LC Auto slide ${i + 1}`}
              className="hero-slide-img"
              style={{
                position: "absolute",
                top: 0, left: 0,
                width: "100%", height: "100%",
                objectFit: "cover",
                opacity: i === slide ? 1 : 0,
                transition: "opacity 1200ms ease-in-out",
              }}
            />
          ))}
        </div>
        {/* Mobile/Tablet: video MP4 */}
        <video
          className="hero-video md:hidden"
          autoPlay
          loop
          muted
          playsInline
          poster={HERO_POSTER}
        >
          <source src={HERO_VIDEO} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-black/30" style={{ zIndex: 1 }} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0C] via-transparent to-transparent" style={{ zIndex: 1 }} />
        <div className="hero-content flex flex-col justify-center" style={{ minHeight: "70vh" }}>
          <div className="text-[9px] sm:text-[10px] md:text-xs tracking-[0.4em] text-[#E1BC56] mb-2 md:mb-3 font-display">LC AUTO · ALTAMURA (BA)</div>
          <h1 className="font-display font-extrabold tracking-widest text-white leading-tight" data-testid="hero-title" style={{ fontSize: "clamp(1.5rem, 5vw, 4.5rem)" }}>
            LA TUA PROSSIMA <span className="gold-text">AUTO</span> AD ALTAMURA
          </h1>
          <p className="text-zinc-200 mt-2 md:mt-4">
            {HERO_TAGS.join(" · ")}
          </p>
          <div className="mt-4 md:mt-8 flex flex-col sm:flex-row gap-2 md:gap-3">
            <Link to="/parco-veicoli" className="cmc-btn-gold px-4 md:px-8 py-2 md:py-3.5 rounded-sm inline-flex items-center justify-center gap-1.5 text-[10px] md:text-sm whitespace-nowrap" data-testid="hero-cta-parco">
              ESPLORA IL PARCO AUTO <ChevronRight className="w-3.5 h-3.5 md:w-4 md:h-4" />
            </Link>
            <a href={whatsappLink()} target="_blank" rel="noreferrer" className="cmc-btn-outline px-4 md:px-8 py-2 md:py-3.5 rounded-sm text-[10px] md:text-sm text-center whitespace-nowrap">CHATTA SU WHATSAPP</a>
          </div>
          {/* Desktop slider dots */}
          <div className="hidden md:flex gap-2 mt-8">
            {HERO_DESKTOP_SLIDES.map((src, i) => (
              <button key={src} onClick={() => setSlide(i)}
                className={`h-1 transition-all ${i === slide ? "w-10 bg-[#E1BC56]" : "w-5 bg-white/30"}`}
                aria-label={`slide-${i}`} data-testid={`hero-dot-${i}`} />
            ))}
          </div>
        </div>
      </section>

      {/* QUICK SEARCH */}
      <section className="max-w-6xl mx-auto px-6 mt-8 relative z-20" data-testid="quick-search">
        <form onSubmit={search} className="backdrop-blur-xl bg-[#0B0B0C]/90 border border-[#E1BC56]/30 rounded-lg p-5 md:p-6 grid grid-cols-2 md:grid-cols-4 gap-3 shadow-2xl">
          <select value={f.marca} onChange={(e) => setF({ ...f, marca: e.target.value })} className="bg-[#121214] border border-white/10 rounded-sm px-3 py-3 text-sm text-white focus:border-[#E1BC56] focus:outline-none" data-testid="qs-marca">
            <option value="">Marca (Tutte)</option>
            {brands.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
          <select value={f.alimentazione} onChange={(e) => setF({ ...f, alimentazione: e.target.value })} className="bg-[#121214] border border-white/10 rounded-sm px-3 py-3 text-sm text-white focus:border-[#E1BC56] focus:outline-none" data-testid="qs-alimentazione">
            <option value="">Carburante</option>
            <option>Benzina</option><option>Diesel</option><option>Ibrida</option><option>Elettrica</option>
          </select>
          <select value={f.anno} onChange={(e) => setF({ ...f, anno: e.target.value })} className="bg-[#121214] border border-white/10 rounded-sm px-3 py-3 text-sm text-white focus:border-[#E1BC56] focus:outline-none" data-testid="qs-anno">
            <option value="">Anno da</option>
            {[2015, 2018, 2020, 2022, 2023, 2024].map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <button type="submit" className="cmc-btn-gold rounded-sm py-3" data-testid="qs-submit">FILTRA</button>
        </form>
      </section>

      {/* ULTIMI ARRIVI */}
      <section className="max-w-7xl mx-auto px-6 py-20" data-testid="vetrina-section">
        <div className="text-center mb-10">
          <div className="text-xs tracking-[0.4em] text-[#E1BC56] font-display mb-2 uppercase">
            {new Date().toLocaleDateString("it-IT", { month: "long", year: "numeric" })}
          </div>
          <h2 className="font-display text-4xl md:text-5xl font-extrabold text-white tracking-widest">ULTIMI ARRIVI</h2>
          <p className="text-zinc-400 mt-3 text-sm">Le auto più recenti selezionate personalmente da Luigi Cornacchia</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {vehicles.map((v) => <VehicleCard key={v.id} v={v} />)}
        </div>
        <div className="text-center mt-10">
          <Link to="/parco-veicoli" className="cmc-btn-outline px-8 py-3 rounded-sm inline-flex items-center gap-2 text-xs">VEDI TUTTI I VEICOLI →</Link>
        </div>
      </section>

      {/* DOUBLE CTA banners removed */}

      {/* FINANZIAMENTI */}
      <section className="max-w-7xl mx-auto px-6 pb-16" data-testid="finanziamenti-section">
        <div className="bg-gradient-to-r from-[#121214] via-[#1A1A1E] to-[#121214] border border-[#E1BC56]/30 rounded-lg p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="max-w-2xl">
            <div className="text-xs tracking-[0.4em] text-[#E1BC56] font-display mb-2">FINANZIAMENTI PERSONALIZZATI</div>
            <h2 className="font-display text-2xl md:text-3xl font-extrabold text-white tracking-wider mb-3">ACQUISTA COMODAMENTE A RATE</h2>
            <p className="text-zinc-400 text-sm md:text-base">Studiamo con te la soluzione di finanziamento più adatta: rate su misura, permuta valorizzata e tempi di risposta rapidi grazie ai nostri partner bancari.</p>
          </div>
          <Link to="/contatti" className="cmc-btn-gold px-6 py-3 rounded-sm text-sm whitespace-nowrap" data-testid="cta-finanziamento">RICHIEDI PREVENTIVO</Link>
        </div>
      </section>

      {/* TRUST */}
      <section className="border-y border-white/10 bg-[#0B0B0C]/60" data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-14">
            {[
              { icon: Award, title: "VASTA SELEZIONE", desc: "Auto premium e di lusso selezionate personalmente." },
              { icon: Truck, title: "CONSEGNA IN ITALIA", desc: "Trasporto sicuro con bisarca in tutto il territorio." },
              { icon: ShieldCheck, title: "GARANZIA INCLUSA", desc: "Ogni veicolo con garanzia e storico verificato." },
            ].map((p) => (
              <div key={p.title} className="text-center px-4">
                <p.icon className="w-10 h-10 mx-auto text-[#E1BC56] mb-3" strokeWidth={1.4} />
                <div className="font-display text-sm font-bold tracking-widest text-white mb-2">{p.title}</div>
                <div className="text-sm text-zinc-400">{p.desc}</div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 border-t border-white/10 pt-10">
            <Counter value={20} suffix="+" label="Anni di Esperienza" />
            <Counter value={1200} suffix="+" label="Auto Consegnate" />
            <Counter value={100} suffix="%" label="Clienti Soddisfatti" />
            <Counter value={5} suffix="★" label="Valutazione Google" />
          </div>
        </div>
      </section>

      {/* REVIEWS - Google Badge */}
      <section className="max-w-4xl mx-auto px-6 py-20" data-testid="reviews-section">
        <a href="https://www.google.com/search?q=CMC+Auto+Altamura+recensioni" target="_blank" rel="noreferrer"
          className="block bg-gradient-to-br from-[#121214] via-[#1A1A1E] to-[#121214] border border-[#E1BC56]/30 rounded-lg p-8 md:p-10 text-center hover:border-[#E1BC56]/70 hover:shadow-[0_0_30px_rgba(225,188,86,0.15)] transition"
          data-testid="google-reviews-badge">
          <div className="flex items-center justify-center gap-2 mb-4">
            <span className="text-3xl font-bold" style={{ color: "#4285F4" }}>G</span>
            <span className="text-3xl font-bold" style={{ color: "#EA4335" }}>o</span>
            <span className="text-3xl font-bold" style={{ color: "#FBBC05" }}>o</span>
            <span className="text-3xl font-bold" style={{ color: "#4285F4" }}>g</span>
            <span className="text-3xl font-bold" style={{ color: "#34A853" }}>l</span>
            <span className="text-3xl font-bold" style={{ color: "#EA4335" }}>e</span>
            <span className="text-2xl text-white font-display font-bold ml-2 tracking-wider">Reviews</span>
          </div>
          <div className="flex justify-center gap-1 mb-3">
            {[0,1,2,3,4].map((k) => <Star key={k} className="w-8 h-8 fill-[#E1BC56] text-[#E1BC56]" />)}
          </div>
          <div className="font-display text-4xl md:text-5xl font-extrabold gold-text tracking-widest mb-2">5.0</div>
          <div className="text-xs tracking-[0.4em] text-zinc-400 font-display uppercase mb-4">Valutazione Verificata su Google</div>
          <span className="inline-block cmc-btn-gold px-6 py-2.5 rounded-sm text-xs">LEGGI LE RECENSIONI</span>
        </a>
      </section>
    </div>
  );
};

export default Home;
